"use client";

import { useId, useState, type ReactNode } from "react";

/**
 * Posten-Ablauf-Zeile (HR Onboarding O3a, Musterkatalog M-192 O3a-1/O3a-2, Design-Guide `posten-ablauf-zeile.md`): die Zeile eines Checklisten-Posten mit
 * mehreren Stufen. Kopf = Statuskreis, Titel, Unterzeile („Stufe 2 von 5 · Fällig 05.11.2026“), Pillen (Stand, „Dran: …“); aufgeklappt zeigt sie die
 * Stufen-Timeline (erledigt, aktiv, „Kommt danach“, „Bedingt“ mit dem Satz „Entsteht nur, wenn …“ UNTER der Meta-Zeile) und, in der aktiven Stufe, den
 * Antwort-Block, den der Aufrufer als `zusatz` mitgibt; darunter der zugeklappte Verlauf (Rückfragen als kurzer Faden).
 *
 * Der Baustein entscheidet nichts und prüft keine Rechte: Statuskreis, Zustände, Antwort-Knöpfe und Sätze kommen vom Aufrufer, der sie mit derselben
 * Prüffunktion füllt wie die Action (r109/r110). Zustände sind nie nur Farbe: jede Stufe trägt das Wort (Erledigt, Aktiv, Kommt danach, Bedingt).
 * Ein-Stufen-Posten behalten den Haken (`AbhakZeile`); diese Zeile ist für mehrstufige Posten, deren Haken „alle Stufen erledigt“ vortäuschen würde.
 *
 * Markup: Kette `component/accordion/accordion-always-open` (Kopf) und `data-display/.../data-display-cat-timelines/order-status-timeline` (Stufen),
 * Abweichungen AW-175 (Aufklapp-Zeile mit Timeline als Inhalt), AW-176 (Statuskreis statt Haken), AW-177 (bedingte Stufe: gestrichelter Kreis mit
 * „GitBranch“ und Wort „Bedingt“), AW-178 (Antwort-Block mit linkem Balken). Inline-SVG im Lucide-Pfad (das Paket bindet lucide-react nicht ein).
 */
export type PostenStatus = "offen" | "angefragt" | "erledigt" | "entfaellt";
export type AblaufStufenZustand = "erledigt" | "aktiv" | "kommt" | "bedingt" | "uebersprungen";

export interface AblaufStufe {
  id: string;
  nummer: number;
  /** Art der Stufe als Wort („Bestätigen“, „Auftrag an Stelle“ …). */
  titel: string;
  /** „Zuständig: HR · Fällig 05.11.2026“ bzw. „Erledigt am 05.11.2026 · HR“. */
  meta: string;
  zustand: AblaufStufenZustand;
  /** Nur bedingte Stufen: „Entsteht nur, wenn Zugang zum FEPA-Programm nötig: Ja.“ */
  bedingtSatz?: string;
  /** Symbol der Art (Lucide, `aria-hidden`), vom Aufrufer. */
  symbol?: ReactNode;
  /** Inhalt unter der Meta-Zeile, in der aktiven Stufe der Antwort-Block. */
  zusatz?: ReactNode;
}

const ZUSTAND_WORT: Record<AblaufStufenZustand, string> = {
  erledigt: "Erledigt",
  aktiv: "Aktiv",
  kommt: "Kommt danach",
  bedingt: "Bedingt",
  uebersprungen: "Übersprungen",
};

const SVG = { xmlns: "http://www.w3.org/2000/svg", width: 24, height: 24, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true } as const;

function Haken({ klasse }: { klasse: string }) {
  return (
    <svg {...SVG} className={klasse}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function Verzweigung({ klasse }: { klasse: string }) {
  return (
    <svg {...SVG} className={klasse}>
      <line x1="6" x2="6" y1="3" y2="15" />
      <circle cx="18" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M18 9a9 9 0 0 1-9 9" />
    </svg>
  );
}

/** Kugel je Zustand: feste, vollständige Klassenketten (R8). */
const KUGEL: Record<AblaufStufenZustand, string> = {
  erledigt: "relative z-10 mt-1 flex shrink-0 justify-center items-center size-7 rounded-full text-xs font-semibold bg-primary text-primary-foreground",
  aktiv: "relative z-10 mt-1 flex shrink-0 justify-center items-center size-7 rounded-full text-xs font-semibold bg-layer border-2 border-primary text-primary",
  bedingt: "relative z-10 mt-1 flex shrink-0 justify-center items-center size-7 rounded-full text-xs font-semibold bg-layer border border-dashed border-layer-line text-muted-foreground-1",
  uebersprungen: "relative z-10 mt-1 flex shrink-0 justify-center items-center size-7 rounded-full text-xs font-semibold bg-muted text-muted-foreground-1",
  kommt: "relative z-10 mt-1 flex shrink-0 justify-center items-center size-7 rounded-full text-xs font-semibold bg-layer border border-layer-line text-layer-foreground",
};

function Kugel({ s }: { s: AblaufStufe }) {
  return (
    <span className={KUGEL[s.zustand]} aria-hidden="true">
      {s.zustand === "erledigt" ? <Haken klasse="size-4" /> : s.zustand === "bedingt" ? <Verzweigung klasse="size-4" /> : s.nummer}
    </span>
  );
}

/** Statuskreis im Kopf: leer = offen, Ring = angefragt, gefüllt mit Haken = erledigt, gedämpft mit Strich = entfällt. Das Wort steht in der Pille daneben. */
function Statuskreis({ status }: { status: PostenStatus }) {
  if (status === "erledigt") {
    return (
      <span className="shrink-0 size-7 inline-flex justify-center items-center rounded-full bg-primary text-primary-foreground" aria-hidden="true">
        <Haken klasse="size-4" />
      </span>
    );
  }
  if (status === "entfaellt") {
    return (
      <span className="shrink-0 size-7 inline-flex justify-center items-center rounded-full bg-muted text-muted-foreground-1" aria-hidden="true">
        <svg {...SVG} className="size-4">
          <path d="M5 12h14" />
        </svg>
      </span>
    );
  }
  return <span className={status === "angefragt" ? "shrink-0 size-7 rounded-full border-2 border-primary bg-layer" : "shrink-0 size-7 rounded-full border-2 border-line-3 bg-layer"} aria-hidden="true" />;
}

export function PostenAblaufZeile({
  id,
  titel,
  unterzeile,
  status,
  pillen,
  stufen,
  verlauf,
  verlaufAnzahl = 0,
  offenStart = false,
  offen: offenVorgabe,
  beimUmschalten,
  meldung,
}: {
  id: string;
  titel: string;
  unterzeile?: string;
  status: PostenStatus;
  /** Pillen rechts vom Titel (Stand, „Dran: …“, Überfällig). */
  pillen?: ReactNode;
  stufen: AblaufStufe[];
  /** Inhalt des zugeklappten Verlaufs (Rückfragen als Faden). */
  verlauf?: ReactNode;
  verlaufAnzahl?: number;
  offenStart?: boolean;
  /** Gesteuert (z. B. Öffnen über `?posten=` in der Adresse). */
  offen?: boolean;
  beimUmschalten?: (offen: boolean) => void;
  /** Satz am Posten (Ablehnung oder Fehlschlag, r105), `role="status"`. */
  meldung?: ReactNode;
}) {
  const [offenIntern, setOffen] = useState(offenStart);
  const [verlaufOffen, setVerlaufOffen] = useState(false);
  const offen = offenVorgabe ?? offenIntern;
  const schluessel = useId();
  const umschalten = () => {
    const neu = !offen;
    setOffen(neu);
    beimUmschalten?.(neu);
  };
  return (
    <>
      {/* Preline: component/accordion/accordion-always-open (Kopf), data-display/data-display-sub-operational-activity-views/data-display-cat-timelines/order-status-timeline (Stufen) */}
      {/* Abweichung AW-175: Aufklapp-Zeile mit Stufen-Timeline als Inhalt; AW-176: Statuskreis statt Haken; AW-177: bedingte Stufe; AW-178: Antwort-Block mit linkem Balken */}
      <div id={id} className={status === "erledigt" || status === "entfaellt" ? "bg-muted" : "bg-layer"} data-posten-ablauf={status}>
        <h3>
          <button
            type="button"
            className="w-full py-3 px-4 flex items-start gap-x-3 text-start hover:bg-muted-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-focus"
            aria-expanded={offen}
            aria-controls={`${schluessel}-inhalt`}
            id={`${schluessel}-kopf`}
            onClick={umschalten}
          >
            <Statuskreis status={status} />
            <span className="grow min-w-0">
              <span className={status === "erledigt" || status === "entfaellt" ? "block text-base font-normal text-muted-foreground-1" : "block text-base font-normal text-foreground"}>{titel}</span>
              {unterzeile && <span className="block text-xs font-normal text-muted-foreground-1">{unterzeile}</span>}
              {pillen && <span className="mt-1.5 flex flex-wrap items-center gap-1.5">{pillen}</span>}
            </span>
            <svg {...SVG} className={offen ? "mt-1 shrink-0 size-4 text-muted-foreground-1 rotate-90" : "mt-1 shrink-0 size-4 text-muted-foreground-1"}>
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </h3>
        {meldung && (
          <div role="status" className="px-4 pb-2">
            {meldung}
          </div>
        )}
        <div id={`${schluessel}-inhalt`} role="region" aria-labelledby={`${schluessel}-kopf`} hidden={!offen} className="px-4 pb-4">
          {offen && (
            <>
              <ol className="ms-1">
                {stufen.map((s, i) => (
                  <li key={s.id} className="relative flex gap-x-3" data-stufe-zustand={s.zustand}>
                    {i < stufen.length - 1 && <span className="absolute top-8 -bottom-1 start-3.5 border-s border-line-2" aria-hidden="true" />}
                    <Kugel s={s} />
                    <div className="grow min-w-0 pb-4">
                      <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-foreground">
                        {s.symbol && <span className="text-muted-foreground-1">{s.symbol}</span>}
                        <span className="font-semibold">{s.titel}</span>
                        <span className={s.zustand === "aktiv" ? "text-xs font-medium text-primary" : "text-xs font-medium text-muted-foreground-1"}>{ZUSTAND_WORT[s.zustand]}</span>
                      </p>
                      <p className="text-xs text-muted-foreground-1">{s.meta}</p>
                      {s.bedingtSatz && <p className="text-xs text-muted-foreground-1">{s.bedingtSatz}</p>}
                      {s.zusatz && <div className="mt-2">{s.zusatz}</div>}
                    </div>
                  </li>
                ))}
              </ol>
              {verlauf && (
                <div className="ms-1 border-t border-line-2 pt-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-x-1.5 text-xs font-medium text-muted-foreground-1 hover:text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-lg"
                    aria-expanded={verlaufOffen}
                    aria-controls={`${schluessel}-verlauf`}
                    onClick={() => setVerlaufOffen(!verlaufOffen)}
                  >
                    <svg {...SVG} className={verlaufOffen ? "size-3.5 rotate-90" : "size-3.5"}>
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                    Verlauf{verlaufAnzahl > 0 ? ` · ${verlaufAnzahl}` : ""}
                  </button>
                  <div id={`${schluessel}-verlauf`} hidden={!verlaufOffen} className="mt-2">
                    {verlaufOffen && verlauf}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
